import { useMemo } from 'react';
import type { Selection } from 'react-aria-components';
import { Header, Section } from 'react-aria-components';
import { Star } from 'lucide-react';
import { Badge } from 'v5/design-system/Badge';
import { Button } from 'v5/design-system/Button';
import {
  ListBox,
  ListBoxItem,
  ListBoxItemContent
} from 'v5/design-system/ListBox';
import { SimpleTooltip } from 'v5/design-system/Tooltip';
import { cn } from 'v5/platform/dom/cn';
import { ScrollShadowWrapper } from 'v5/primitives/ScrollShadowWrapper';
import {
  FeatureFlagEntry,
  FeatureFlagGroup
} from 'v5/services/front/FeatureManagementService/types';
import { buildFeatureFlagCatalogSections } from './buildFeatureFlagCatalog';

export interface FeatureFlagCatalogProps {
  groups: FeatureFlagGroup[];
  selectedFeatureId: string | null;
  onSelect: (featureId: string) => void;
  search: string;
  dirtyFeatureIds: ReadonlySet<string>;
  starredFeatureIds: readonly string[];
  onToggleStarred: (featureId: string) => void;
}

function PersistenceBadges({ entry }: { entry: FeatureFlagEntry }) {
  const hasServer = entry.config.legacy.supported;
  const hasFrontend = entry.config.frontend.supported;

  return (
    <span className="flex shrink-0 items-center gap-1">
      {hasServer && (
        <Badge variant="attention" tone="subtle" size="sm">
          Server
        </Badge>
      )}
      {hasFrontend && (
        <Badge variant="success" tone="subtle" size="sm">
          This browser
        </Badge>
      )}
    </span>
  );
}

function StarButton({
  starred,
  featureName,
  onToggle
}: {
  starred: boolean;
  featureName: string;
  onToggle: () => void;
}) {
  return (
    <SimpleTooltip title={starred ? 'Remove from starred' : 'Star this flag'}>
      <Button
        type="button"
        variant="ghost"
        layout="icon"
        size="xs"
        aria-label={starred ? `Unstar ${featureName}` : `Star ${featureName}`}
        aria-pressed={starred}
        className={cn(
          'size-6 shrink-0 text-fg-subtle aria-pressed:border-transparent aria-pressed:bg-transparent',
          starred
            ? 'text-sun opacity-100'
            : 'opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 group-aria-selected:opacity-100'
        )}
        onPointerDown={event => {
          event.preventDefault();
          event.stopPropagation();
        }}
        onClick={event => {
          event.preventDefault();
          event.stopPropagation();
          onToggle();
        }}
      >
        <Star className={cn('size-4', starred && 'fill-current')} />
      </Button>
    </SimpleTooltip>
  );
}

export function FeatureFlagCatalog({
  groups,
  selectedFeatureId,
  onSelect,
  search,
  dirtyFeatureIds,
  starredFeatureIds,
  onToggleStarred
}: FeatureFlagCatalogProps) {
  const query = search.trim().toLowerCase();
  const starredSet = useMemo(
    () => new Set(starredFeatureIds),
    [starredFeatureIds]
  );

  const filteredGroups = useMemo(
    () => buildFeatureFlagCatalogSections(groups, query, starredFeatureIds),
    [groups, query, starredFeatureIds]
  );

  const selectedKeys = useMemo(
    () =>
      selectedFeatureId ? new Set([selectedFeatureId]) : new Set<string>(),
    [selectedFeatureId]
  );

  const handleSelectionChange = (keys: Selection) => {
    if (keys === 'all' || keys.size === 0) {
      return;
    }
    const key = [...keys][0];
    if (key == null) {
      return;
    }
    onSelect(String(key));
  };

  return (
    <ScrollShadowWrapper
      renderTopShadow={false}
      className="min-h-0 flex-1 overflow-y-auto with-styled-scrollbars"
    >
      {filteredGroups.length === 0 ? (
        <p className="px-4 py-6 text-sm text-fg-muted">No flags match.</p>
      ) : (
        <ListBox
          aria-label="Feature flags"
          selectionMode="single"
          selectedKeys={selectedKeys}
          onSelectionChange={handleSelectionChange}
          className="divide-y-0"
        >
          {filteredGroups.map(group => (
            <Section key={group.name} id={group.name}>
              <Header
                className={cn(
                  '-header sticky top-0 z-[1] bg-bg-default px-4 py-2 text-xs font-semibold uppercase tracking-wide text-fg-muted',
                  '[[data-has-top-shadow]_&]:shadow-sm'
                )}
              >
                {group.name === 'Starred' ? (
                  <span className="flex items-center gap-1.5">
                    <Star className="size-3 fill-current text-sun" />
                    Starred
                  </span>
                ) : (
                  group.name
                )}
              </Header>
              {group.entries.map(entry => {
                const dirty = dirtyFeatureIds.has(entry.featureId);
                const starred = starredSet.has(entry.featureId);
                return (
                  <ListBoxItem
                    key={entry.featureId}
                    id={entry.featureId}
                    textValue={`${entry.featureName}. ${entry.description}`}
                    className="gap-3 px-3 py-3"
                  >
                    <ListBoxItemContent
                      showCheckmark={false}
                      icon={
                        dirty ? (
                          <span
                            className="size-2 shrink-0 rounded-full bg-fg-attention"
                            aria-label="Unsaved changes"
                          />
                        ) : (
                          <span className="size-2 shrink-0" aria-hidden />
                        )
                      }
                      primary={entry.featureName}
                      secondary={entry.description || undefined}
                      extra={
                        <span className="flex shrink-0 items-center gap-1">
                          <StarButton
                            starred={starred}
                            featureName={entry.featureName}
                            onToggle={() => onToggleStarred(entry.featureId)}
                          />
                          <PersistenceBadges entry={entry} />
                        </span>
                      }
                    />
                  </ListBoxItem>
                );
              })}
            </Section>
          ))}
        </ListBox>
      )}
    </ScrollShadowWrapper>
  );
}
